import * as React from "react"
import { Link, router, usePage } from "@inertiajs/react"
import {
  ChevronsLeft,
  ChevronsRight,
  Home,
  Building2,
  BookOpen,
  ClipboardList,
  FileSpreadsheet,
  FolderKanban,
  HardHat,
  LogOut,
  Menu,
  Settings,
  Shield,
  User,
  X,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"
import { ThemeToggle } from "@/components/ui/theme-toggle"

const STORAGE_KEY = "main-nav-open"
const DEFAULT_BRAND = "Contracting OS"

export type NavItemDef = {
  href: string
  icon: React.ComponentType<{ className?: string }>
  label: string
  match: (url: string) => boolean
}

const BASE_NAV_ITEMS: NavItemDef[] = [
  {
    href: "/dashboard",
    icon: Home,
    label: "Home",
    match: (url) => url === "/" || url.startsWith("/dashboard"),
  },
]

function useMainNavOpen() {
  const [open, setOpen] = React.useState<boolean>(true)
  React.useEffect(() => {
    if (typeof window === "undefined") return
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored !== null) setOpen(stored === "true")
  }, [])
  React.useEffect(() => {
    if (typeof window === "undefined") return
    window.localStorage.setItem(STORAGE_KEY, String(open))
  }, [open])
  return [open, setOpen] as const
}

export function MainNav({
  items,
  brandHref = "/dashboard",
}: {
  items?: NavItemDef[]
  brandHref?: string
} = {}) {
  const [open, setOpen] = useMainNavOpen()
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const mobileTriggerRef = React.useRef<HTMLButtonElement>(null)
  const mobilePanelRef = React.useRef<HTMLElement>(null)
  const { props } = usePage<PageProps>()
  const { url } = usePage()
  const brand = props.current_user?.company?.display_name || DEFAULT_BRAND
  const defaultItems = React.useMemo(() => {
    const resolved = [...BASE_NAV_ITEMS]
    const user = props.current_user
    if (user?.enabled_modules.includes("crm") && user.permissions.includes("crm.view")) {
      resolved.push({ href: "/crm/enquiries", icon: ClipboardList, label: "Enquiries", match: url => url.startsWith("/crm/enquiries") || url.startsWith("/crm/site_visits") })
      resolved.push({ href: "/crm/clients", icon: Building2, label: "Clients", match: url => url.startsWith("/crm/clients") })
    }
    if (user?.enabled_modules.includes("estimating") && user.permissions.includes("estimating.view")) {
      resolved.push({ href: "/estimating/boqs", icon: FileSpreadsheet, label: "BOQs & quotes", match: url => url.startsWith("/estimating") })
    }
    if (user?.enabled_modules.includes("projects") && (user.permissions.includes("projects.view_all") || user.permissions.includes("projects.view_assigned"))) {
      resolved.push({ href: "/projects", icon: FolderKanban, label: "Projects", match: url => url.startsWith("/projects") })
      resolved.push({ href: "/work-catalogue", icon: BookOpen, label: "Work catalogue", match: url => url.startsWith("/work-catalogue") || url.startsWith("/work_types") || url.startsWith("/activity_templates") })
      if (user.permissions.includes("site_execution.report") || user.permissions.includes("site_execution.manage")) {
        resolved.push({ href: "/site/today", icon: HardHat, label: "Today on site", match: url => url.startsWith("/site") })
      }
    }
    return resolved
  }, [props.current_user])
  const resolvedItems = items || defaultItems

  React.useEffect(() => {
    setMobileOpen(false)
  }, [url])

  const closeMobile = React.useCallback(() => {
    setMobileOpen(false)
    window.requestAnimationFrame(() => mobileTriggerRef.current?.focus())
  }, [])

  React.useEffect(() => {
    const panel = mobilePanelRef.current
    if (!mobileOpen || !panel) return
    const focusable = Array.from(panel.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
    focusable[0]?.focus()
    const keepFocusInside = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMobile()
        return
      }
      if (event.key !== "Tab" || focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    panel.addEventListener("keydown", keepFocusInside)
    return () => panel.removeEventListener("keydown", keepFocusInside)
  }, [closeMobile, mobileOpen])

  return (
    <>
      <aside
        className={cn(
          "sticky top-0 z-20 hidden h-screen shrink-0 flex-col border-r border-white/45 bg-background/58 shadow-[inset_-1px_0_0_color-mix(in_oklab,var(--foreground),transparent_94%)] backdrop-blur-2xl transition-[width] duration-200 dark:border-white/8 print:hidden lg:flex",
          open ? "w-56" : "w-14",
        )}
      >
        <RailBody
          open={open}
          onToggle={() => setOpen(!open)}
          items={resolvedItems}
          brandHref={brandHref}
          brand={brand}
        />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 print:hidden lg:hidden">
          <div
            className="absolute inset-0 bg-ink-display/40 backdrop-blur-sm"
            onClick={closeMobile}
            aria-hidden
          />
          <aside ref={mobilePanelRef} role="dialog" aria-modal="true" aria-label="Main navigation" className="absolute left-0 top-0 flex h-full w-[min(20rem,88vw)] flex-col border-r border-white/40 bg-background/82 shadow-xl backdrop-blur-2xl dark:border-white/10">
            <div className="flex h-14 shrink-0 items-center justify-between border-b border-hairline px-4">
              <span className="font-display text-sm font-semibold text-ink-display">
                {brand}
              </span>
              <button
                type="button"
                onClick={closeMobile}
                className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-ink-muted hover:bg-surface hover:text-ink-display"
                aria-label="Close navigation"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <RailNav open items={resolvedItems} onClose={closeMobile} />
            <div className="border-t border-hairline p-2">
              <UserMenu open />
            </div>
          </aside>
        </div>
      )}

      <aside id="mobile-navigation-fallback" popover="auto" role="dialog" aria-modal="true" aria-label="Main navigation" className="fixed inset-y-0 left-0 z-50 w-[min(20rem,88vw)] flex-col bg-page shadow-xl backdrop:bg-ink-display/40 backdrop:backdrop-blur-sm lg:hidden">
        <div className="flex h-14 items-center border-b border-hairline px-4"><span className="font-display text-sm font-semibold text-ink-display">{brand}</span></div>
        <nav className="p-2"><a href="/site/today" className="block rounded-md p-3 text-sm text-ink-display">Today on site</a></nav>
      </aside>

      <button
        ref={mobileTriggerRef}
        type="button"
        onPointerDown={() => setMobileOpen(true)}
        onClick={() => setMobileOpen(true)}
        {...({ popovertarget: "mobile-navigation-fallback" } as React.HTMLAttributes<HTMLButtonElement>)}
        className="fixed right-3 top-[max(.75rem,env(safe-area-inset-top))] z-30 inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-hairline bg-page/95 text-ink-body shadow-sm backdrop-blur hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent print:hidden lg:hidden"
        aria-label="Open navigation"
      >
        <Menu className="h-4 w-4" />
      </button>
    </>
  )
}

function RailBody({
  open,
  onToggle,
  items,
  brandHref,
  brand,
}: {
  open: boolean
  onToggle: () => void
  items: NavItemDef[]
  brandHref: string
  brand: string
}) {
  return (
    <>
      <div
        className={cn(
          "flex h-14 shrink-0 items-center gap-3 border-b border-hairline px-3",
          open ? "justify-between" : "justify-center",
        )}
      >
        <Link
          href={brandHref}
          className="flex min-w-0 items-center gap-2 text-ink-display no-underline"
          aria-label={brand}
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent-faded font-display text-sm font-semibold text-accent-display">
            {brand.charAt(0)}
          </span>
          {open && (
            <span className="truncate font-display text-sm font-semibold">
              {brand}
            </span>
          )}
        </Link>
        {open && (
          <button
            type="button"
            onClick={onToggle}
            className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-ink-muted hover:bg-surface hover:text-ink-display"
            aria-label="Collapse sidebar"
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
        )}
      </div>

      <RailNav open={open} items={items} />

      {!open && (
        <div className="border-t border-hairline p-2">
          <button
            type="button"
            onClick={onToggle}
            className="flex h-9 w-full cursor-pointer items-center justify-center rounded-md text-ink-muted hover:bg-surface hover:text-ink-display"
            aria-label="Expand sidebar"
          >
            <ChevronsRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex items-center gap-1 border-t border-border p-2">
        {open && <ThemeToggle />}
        <UserMenu open={open} />
      </div>
    </>
  )
}

function RailNav({
  open,
  items,
  onClose,
}: {
  open: boolean
  items: NavItemDef[]
  onClose?: () => void
}) {
  const { url } = usePage()
  return (
    <nav
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-1 p-2 text-sm",
        open ? "overflow-x-hidden overflow-y-auto" : "overflow-visible",
      )}
    >
      {items.map((item) => (
        <NavItem
          key={item.href}
          href={item.href}
          icon={item.icon}
          label={item.label}
          active={item.match(url)}
          open={open}
          onClick={onClose}
        />
      ))}
    </nav>
  )
}

function NavItem({
  href,
  icon: Icon,
  label,
  active,
  open,
  onClick,
}: {
  href: string
  icon: React.ComponentType<{ className?: string }>
  label: string
  active?: boolean
  open: boolean
  onClick?: () => void
}) {
  return (
    <div className="group/nav-item relative">
      <Link
        href={href}
        onClick={onClick}
        aria-current={active ? "page" : undefined}
        aria-label={open ? undefined : label}
        className={cn(
          "flex items-center gap-3 rounded-md no-underline",
          open ? "px-3 py-2" : "mx-auto h-9 w-9 justify-center",
          active
            ? "bg-primary/12 text-foreground shadow-[inset_3px_0_0_var(--primary)]"
            : "text-muted-foreground hover:bg-background/55 hover:text-foreground",
        )}
      >
        <Icon className="h-4 w-4 shrink-0" />
        {open && <span className="truncate">{label}</span>}
      </Link>
      {!open && (
        <span
          role="tooltip"
          className="pointer-events-none absolute left-full top-1/2 z-50 ml-[13px] -translate-y-1/2 whitespace-nowrap rounded-md border border-hairline bg-page px-2 py-1 text-xs font-medium text-ink-display opacity-0 shadow-sm transition-opacity group-hover/nav-item:opacity-100"
        >
          {label}
        </span>
      )}
    </div>
  )
}

function UserMenu({ open }: { open: boolean }) {
  const { props } = usePage<PageProps>()
  const email = props.current_user?.email ?? ""
  const isAdmin = props.current_user?.admin ?? false
  const initial = email.charAt(0).toUpperCase() || "?"

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={open ? undefined : email || "Account"}
          className={cn(
            "group/user relative flex w-full cursor-pointer items-center gap-3 rounded-md text-left text-ink-body hover:bg-surface hover:text-ink-display focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
            open ? "px-2 py-2" : "h-10 justify-center",
          )}
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-faded text-xs font-semibold text-accent-display">
            {initial}
          </span>
          {open ? (
            <span className="min-w-0 flex-1 truncate text-sm">{email}</span>
          ) : (
            <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md border border-hairline bg-page px-2 py-1 text-xs font-medium text-ink-display opacity-0 shadow-md transition-opacity group-hover/user:opacity-100">
              {email}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-56">
        <DropdownMenuLabel className="normal-case tracking-normal">
          <span className="block text-[10px] uppercase tracking-wider text-ink-muted">
            Signed in as
          </span>
          <span className="block truncate text-xs font-medium text-ink-display">
            {email}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/profile" className="no-underline">
            <User /> Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/company/settings" className="no-underline">
            <Settings /> Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {isAdmin && (
          <DropdownMenuItem asChild>
            <Link href="/admin/users" className="no-underline">
              <Shield /> Admin
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault()
            router.delete("/logout")
          }}
        >
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
